/**
 * Design panel actions use the viewer's wrapper operations.
 * Provenance and ContentSlot identify wrappers in the scene, including after reload.
 */

import type { ToolContext } from './agent-helpers';
import type { SceneObject } from 'trace-viewer/scene';
import { findWrapper, changeWrapperSet, removeWrapper as unwrap } from 'trace-viewer/editor';
import { Provenance } from 'trace-viewer/behaviors';
import { declareTheme, runWrapperAction, stageWrapperAction } from './tools/template-tools';
import { placeCatalogAsset, type PlaceCatalogAssetInput, type PlaceCatalogAssetResult } from './tools/place-catalog-asset';
import { getStyle, listStyles, type LibraryStyle } from './styles/library';
import { WRAPPER_LIBRARY, getWrapper, COMPONENT_MATERIALS } from './templates/wrappers';
import { getWrapperLibrary } from './wrapper-library';
import { copyForInsideEdit } from '../cms/reference-edit-guard';

export interface DesignActionResult {
    ok: boolean;
    message: string;
}

export interface StageInfo {
    stageRoot: SceneObject;
    wrapperId: string | null;
    set: string | undefined;
    size: string | undefined;
}

/** Read the scene and the placed asset's metadata; no staging state is kept here. */
export async function currentStage(target: SceneObject): Promise<StageInfo | null> {
    if (target.transform.isDisposed()) return null;
    const root = findWrapper(target);
    if (!root) return null;
    const assetUrl = root.getBehavior(Provenance)!.getData().placedFrom!;
    const key = await getWrapperLibrary().then(library => library.describe(assetUrl)).catch(() => null);
    if (target.transform.isDisposed() || root.transform.isDisposed() || findWrapper(target) !== root) return null;
    return { stageRoot: root, wrapperId: key?.family ?? null, size: key?.size, set: key?.set };
}

function referenceRefusal(target: SceneObject): DesignActionResult {
    return { ok: false, message: `"${target.name}" is inside a library asset that could not be copied into this scene, so it was not changed.` };
}

export function listDesignStyles(): LibraryStyle[] {
    return listStyles();
}

export async function applyStyle(target: SceneObject, styleSlug: string): Promise<DesignActionResult> {
    const style = getStyle(styleSlug);
    if (!style) return { ok: false, message: `No style "${styleSlug}".` };
    if (!await copyForInsideEdit(target)) return referenceRefusal(target);
    await declareTheme(target, style, undefined, { record: true });
    return { ok: true, message: `Applied "${style.meta.name}" to "${target.name}".` };
}

export function listWrapperChoices(): { id: string; label: string }[] {
    return WRAPPER_LIBRARY.map(w => ({ id: w.id, label: w.label }));
}

export async function stageWrapper(
    ctx: ToolContext,
    selected: SceneObject,
    wrapperId: string,
    opts: { style?: string; set?: string; size?: string } = {},
): Promise<DesignActionResult> {
    return stageWrapperAction(ctx, selected, wrapperId, opts);
}

export async function removeWrapper(ctx: ToolContext, selected: SceneObject): Promise<DesignActionResult> {
    const wrapper = findWrapper(selected);
    if (!wrapper) return { ok: false, message: 'No wrapper is staged around this object.' };
    const name = wrapper.name;
    const stage = await currentStage(wrapper);
    const wrapperId = stage?.wrapperId ?? wrapper.getBehavior(Provenance)!.getData().placedFrom!;
    return runWrapperAction(ctx, wrapper, () => unwrap(wrapper), () => `Removed stage "${name}".`, wrapperId);
}

export async function swapDesignSet(ctx: ToolContext, selected: SceneObject, set: string): Promise<DesignActionResult> {
    const stage = await currentStage(selected);
    if (!stage) return { ok: false, message: 'No wrapper is staged around this object — pick a wrapper first, then a form.' };
    const library = await getWrapperLibrary();
    const wrapperId = stage.wrapperId ?? stage.stageRoot.getBehavior(Provenance)!.getData().placedFrom!;
    return runWrapperAction(ctx, stage.stageRoot, () => changeWrapperSet(stage.stageRoot, set, library),
        result => `Staged "${wrapperId}" (${stage.size}, set ${set}) as "${result.wrapper!.name}".`, wrapperId);
}

/** Design sets with geometry in the existing Form control's inventory. */
export const KNOWN_DESIGN_SETS: readonly string[] = (() => {
    const hasRealGeometry = new Map<string, boolean>();
    for (const filename of Object.keys(COMPONENT_MATERIALS)) {
        const match = /^SM_([A-Za-z0-9]+)_/.exec(filename);
        if (!match) continue; // non-prefixed base kit pieces (Panel.glb, BtnRect.glb, ...) — the "Core" fallback, not a named set
        const name = match[1];
        const isDoodadOnly = filename.includes('_Doodad_');
        hasRealGeometry.set(name, (hasRealGeometry.get(name) ?? false) || !isDoodadOnly);
    }
    return [...hasRealGeometry.entries()]
        .filter(([, real]) => real)
        .map(([name]) => name)
        .sort();
})();

/** Which wrapper (if any) `getWrapper` knows about — used to validate panel input defensively. */
export function isKnownWrapper(id: string): boolean {
    return !!getWrapper(id);
}

// ── Library ──────────────────────────────────────────────────

/** Place a catalog/library row into the scene — thin re-export so the panel
 * imports one module for every Design action. */
export async function placeLibraryItem(input: PlaceCatalogAssetInput, ctx: ToolContext): Promise<PlaceCatalogAssetResult> {
    return placeCatalogAsset(input, ctx);
}
